import { MapPinIcon, PencilSquareIcon, PlusIcon, TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Chip, ConfirmDialog, DataTable, LinkButton, notify, PageHeader } from '../../components/ui';
import type { Column } from '../../components/ui';
import { OWNER_ABOVE } from '../../constants/roles';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useApiError } from '../../hooks/useApiError';
import useAuth from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import { getTranslation } from '../../utils/translation.utils';
import type { LocationRecord } from './apiTypes';
import { useRequest } from './useRequest';

interface Row extends LocationRecord {
    tableCount: number | null;
}

const LocationList = () => {
    const { t, i18n } = useTranslation(['locations', 'common']);
    usePageTitle(t('list.title'));
    const { hasRole } = useAuth();
    const canEdit = hasRole(OWNER_ABOVE);
    const { restaurantId } = useActiveLocation();
    const apiError = useApiError();
    const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
    const [deleting, setDeleting] = useState(false);

    const { data, state, retry, reload } = useRequest(restaurantId ? `list:${restaurantId}` : null, async () => {
        const { data: locations } = await api.get<LocationRecord[]>(`/restaurants/${restaurantId}/locations`);
        return Promise.all(
            locations.map(async (l): Promise<Row> => {
                const tableCount = await api
                    .get<unknown[]>(`/locations/${l.id}/tables`)
                    .then((r) => r.data.length)
                    .catch(() => null);
                return { ...l, tableCount };
            })
        );
    });

    const menuName = (l: Row) =>
        l.activeMenu ? getTranslation(l.activeMenu.translations, 'name', l.activeMenu.name, i18n.language) : '';

    const columns: Column<Row>[] = [
        { id: 'name', header: t('list.columns.name'), priority: 'primary', cell: (l) => l.name, sortValue: (l) => l.name },
        { id: 'address', header: t('list.columns.address'), priority: 'secondary', cell: (l) => <span className="text-muted">{l.address}</span>, sortValue: (l) => l.address },
        {
            id: 'menu',
            header: t('list.columns.menu'),
            priority: 'meta',
            sortValue: menuName,
            cell: (l) =>
                l.activeMenu ? (
                    <Link to={`/menus/${l.activeMenu.id}`} className="font-medium text-link underline-offset-2 hover:underline">
                        {menuName(l)}
                    </Link>
                ) : (
                    <Chip variant="action">{t('list.noMenu')}</Chip>
                ),
        },
        {
            id: 'tables',
            header: t('list.columns.tables'),
            priority: 'meta',
            align: 'end',
            sortValue: (l) => l.tableCount ?? -1,
            cell: (l) => l.tableCount ?? '—',
        },
    ];

    const remove = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/locations/${pendingDelete.id}`);
            notify.success(t('list.deleted', { name: pendingDelete.name }));
            setPendingDelete(null);
            reload();
        } catch (e) {
            notify.error(apiError(e));
        } finally {
            setDeleting(false);
        }
    };

    const addButton = canEdit && (
        <LinkButton to="/locations/create" variant="primary" icon={<PlusIcon className="size-5" aria-hidden="true" />}>
            {t('list.add')}
        </LinkButton>
    );

    return (
        <>
            <PageHeader title={t('list.title')} actions={data && data.length > 0 ? addButton : undefined} />
            <DataTable
                rows={data ?? []}
                columns={columns}
                getRowId={(l) => l.id}
                rowHref={(l) => `/locations/${l.id}`}
                rowLabel={(l) => l.name}
                rowActions={(l) => [
                    { id: 'open', label: t('list.open'), icon: ArrowTopRightOnSquareIcon, to: `/locations/${l.id}` },
                    ...(canEdit
                        ? [
                              { id: 'edit', label: t('common:ui.edit'), icon: PencilSquareIcon, to: `/locations/edit/${l.id}` },
                              { id: 'delete', label: t('common:ui.delete'), icon: TrashIcon, tone: 'danger' as const, separated: true, onSelect: () => setPendingDelete(l) },
                          ]
                        : []),
                ]}
                state={state}
                onRetry={retry}
                caption={t('list.title')}
                empty={{
                    icon: MapPinIcon,
                    title: t('list.emptyTitle'),
                    body: canEdit ? t('list.emptyBody') : t('list.emptyBodyReadOnly'),
                    action: addButton || undefined,
                }}
            />
            <ConfirmDialog
                open={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
                busy={deleting}
                title={t('list.deleteTitle', { name: pendingDelete?.name ?? '' })}
            >
                {t('list.deleteBody')}
            </ConfirmDialog>
        </>
    );
};

export default LocationList;
